import React from 'react';
import { 
  Users, MessageSquare, FileVideo, DollarSign, Heart, 
  UserPlus, Mail, Phone, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

const UserDetailsDialog = ({
  open,
  onOpenChange,
  userDetails,
  getRoleBadgeColor
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center text-white font-bold text-xl">
              {userDetails?.user?.username?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-xl">{userDetails?.user?.username}</p>
              <p className="text-sm text-gray-400">{userDetails?.user?.email}</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {userDetails && (
          <Tabs defaultValue="overview" className="mt-4">
            <TabsList className="bg-gray-800">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="content">Content ({userDetails.content_count})</TabsTrigger>
              <TabsTrigger value="transactions">Transactions ({userDetails.transaction_count})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="mt-4">
              <OverviewTab userDetails={userDetails} getRoleBadgeColor={getRoleBadgeColor} />
            </TabsContent>
            
            <TabsContent value="content" className="mt-4">
              <ContentTab content={userDetails.content} />
            </TabsContent>
            
            <TabsContent value="transactions" className="mt-4">
              <TransactionsTab transactions={userDetails.transactions} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

const OverviewTab = ({ userDetails, getRoleBadgeColor }) => (
  <div className="grid grid-cols-2 gap-4">
    {/* User Info */}
    <Card className="bg-gray-800/50 border-gray-700">
      <CardHeader>
        <CardTitle className="text-sm text-gray-400">Profile Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          <span className="text-gray-400">Display Name:</span>
          <span className="text-white">{userDetails.user.display_name || 'Not set'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-gray-500" />
          <span className="text-gray-400">Email:</span>
          <span className="text-white">{userDetails.user.email}</span>
        </div>
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-gray-500" />
          <span className="text-gray-400">Phone:</span>
          <span className="text-white">{userDetails.user.phone || 'Not set'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-gray-500" />
          <span className="text-gray-400">Role:</span>
          <Badge className={`${getRoleBadgeColor(userDetails.user.role)} text-white`}>
            {userDetails.user.role}
          </Badge>
        </div>
      </CardContent>
    </Card>
    
    {/* Social Stats */}
    <Card className="bg-gray-800/50 border-gray-700">
      <CardHeader>
        <CardTitle className="text-sm text-gray-400">Social Statistics</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4">
        <StatCard icon={UserPlus} color="text-blue-400" value={userDetails.social.followers} label="Followers" />
        <StatCard icon={Users} color="text-green-400" value={userDetails.social.following} label="Following" />
        <StatCard icon={Heart} color="text-red-400" value={userDetails.social.likes_received} label="Likes Received" />
        <StatCard icon={MessageSquare} color="text-purple-400" value={userDetails.messages.sent} label="Messages Sent" />
      </CardContent>
    </Card>
    
    {/* Subscription */}
    <Card className="bg-gray-800/50 border-gray-700 col-span-2">
      <CardHeader>
        <CardTitle className="text-sm text-gray-400">Subscription Status</CardTitle>
      </CardHeader>
      <CardContent>
        {userDetails.subscription ? (
          <div className="flex items-center justify-between">
            <div>
              <Badge className="bg-gradient-to-r from-yellow-400 to-purple-500">
                {userDetails.subscription.plan_id?.toUpperCase() || 'UNKNOWN'}
              </Badge>
              <p className="text-sm text-gray-400 mt-1">
                Status: {userDetails.subscription.status}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Expires:</p>
              <p className="text-white">
                {new Date(userDetails.subscription.current_period_end).toLocaleDateString()}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-gray-400">No active subscription</p>
        )}
      </CardContent>
    </Card>
  </div>
);

const StatCard = ({ icon: Icon, color, value, label }) => (
  <div className="text-center p-3 bg-gray-900 rounded-lg">
    <Icon className={`w-5 h-5 ${color} mx-auto mb-1`} />
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-gray-400">{label}</p>
  </div>
);

const ContentTab = ({ content }) => (
  <div className="space-y-3 max-h-96 overflow-y-auto">
    {content.length === 0 ? (
      <p className="text-center text-gray-400 py-8">No content uploaded</p>
    ) : (
      content.map((item) => (
        <Card key={item.id} className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileVideo className="w-8 h-8 text-purple-400" />
              <div>
                <p className="font-medium text-white">{item.title}</p>
                <p className="text-xs text-gray-400">{item.content_type} • {item.visibility}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">{item.view_count || 0} views</p>
              <p className="text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString()}</p>
            </div>
          </CardContent>
        </Card>
      ))
    )}
  </div>
);

const TransactionsTab = ({ transactions }) => (
  <div className="space-y-3 max-h-96 overflow-y-auto">
    {transactions.length === 0 ? (
      <p className="text-center text-gray-400 py-8">No transactions</p>
    ) : (
      transactions.map((tx) => (
        <Card key={tx.id} className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-green-400" />
              <div>
                <p className="font-medium text-white">{tx.type}</p>
                <p className="text-xs text-gray-400">{tx.payment_status}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-green-400">${tx.amount}</p>
              <p className="text-xs text-gray-500">{new Date(tx.created_at).toLocaleDateString()}</p>
            </div>
          </CardContent>
        </Card>
      ))
    )}
  </div>
);

export default UserDetailsDialog;
